import { ReactElement, useRef, useState } from 'react'

import { fontWeightRegular } from '@/constants'
import IconButton from '@components/IconButton/IconButton'
import DialogTitle from '@components/mui/DialogTitle'
import { faCheck, faCopy } from '@fortawesome/pro-light-svg-icons'
import useClipboard from '@hooks/useClipboard'
import {
	Dialog,
	DialogContent,
	Tooltip as MUITooltip,
	TooltipProps,
	Typography,
} from '@mui/material'
import { getTestId } from '@utils/helpers'
import { getTextLengthOfNode } from '@utils/stringHelpers'

type Props = TooltipProps & {
	children: ReactElement
	stringLength?: number
	enableCopy?: boolean
	preformatted?: boolean
}

const DIALOG_HOVER_DELAY_MS = 350

/**
 * Drop-in replacement for MUI's Tooltip. When the title's text is longer than
 * `stringLength`, a scrollable dialog is shown instead of the tooltip. When
 * `enableCopy` is set, the dialog shows a copy-to-clipboard button. When
 * `preformatted` is set, the dialog text is rendered in a `<pre>` tag.
 */
export default function Tooltip({
	title,
	stringLength = undefined,
	enableCopy = false,
	preformatted = false,
	children,
	...rest
}: Props) {
	const testId = getTestId(rest, 'tooltip')
	const [dialogOpen, setDialogOpen] = useState(false)
	const { copy, copied } = useClipboard()
	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

	const isLongText = typeof stringLength === 'number' && getTextLengthOfNode(title) > stringLength

	const handleCopy = () => {
		if (typeof title === 'string') copy(title)
	}

	if (!isLongText) {
		return (
			<MUITooltip title={title} {...rest}>
				{children}
			</MUITooltip>
		)
	}

	const copyButton = enableCopy && (
		<IconButton
			size="small"
			icon={copied ? faCheck : faCopy}
			color={copied ? 'success' : 'inherit'}
			onClick={handleCopy}
			aria-label="Copy to clipboard"
			data-testid={`tooltip-copy-button-${testId}`}
		/>
	)

	const handleMouseEnter = () => {
		hoverTimeoutRef.current = setTimeout(() => setDialogOpen(true), DIALOG_HOVER_DELAY_MS)
	}
	const handleMouseLeave = () => clearTimeout(hoverTimeoutRef.current)
	const handleCloseDialog = () => setDialogOpen(false)

	return (
		<>
			<span
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				style={{ display: 'contents' }}
			>
				{children}
			</span>

			<Dialog open={dialogOpen} onClose={handleCloseDialog} scroll="paper" fullWidth maxWidth="sm">
				<DialogTitle onClose={handleCloseDialog}>{copyButton}</DialogTitle>
				<DialogContent data-testid={testId}>
					{preformatted ? (
						<pre style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', margin: 0 }}>
							{title}
						</pre>
					) : (
						<Typography
							component="div"
							variant="body1"
							fontWeight={fontWeightRegular}
							sx={{ whiteSpace: 'pre-line' }}
						>
							{title}
						</Typography>
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}
